// TechnicalDocumentationPage - Coordination -> Technical documentation
// (guide section 18). The project's documentation as tabs:
//
//   Project at a Glance   first and default: the overview itself
//   All views             every stored view as a card, and New view
//   <one tab per view>    each documented category, named as it was asked
//                         for ("2-inch conduit register", "Roof equipment")
//
// ?tab=glance|views|<viewId> keeps the tab in the address, so a view can
// be linked to directly (the At a Glance tab's documentation menu does).
import PageContainer from "components/PageContainer/PageContainer";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button, Select, Skeleton, Tabs } from "antd";
import { PlusOutlined, ShareAltOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import useLastProject from "components/Coordination/useLastProject";
import GlanceTab from "components/Coordination/Glance/GlanceTab";
import TechnicalView from "components/Coordination/Glance/views/TechnicalView";
import ViewCards from "components/Coordination/Glance/views/ViewCards";
import NewViewModal from "components/Coordination/Glance/views/NewViewModal";
import { useGlanceTheme } from "components/Coordination/Glance/GlanceThemeToggle";
import UploadDocumentsButton from "components/Coordination/Glance/UploadDocumentsButton";
import UploadGuide from "components/Coordination/Glance/UploadGuide";
import ProcessingPanel from "components/Coordination/Glance/views/ProcessingPanel";
import TrackedCategories from "components/Coordination/Glance/views/TrackedCategories";
import ShareResourceModal from "components/Share/ShareResourceModal";
import { viewsApi } from "components/Coordination/Glance/views/viewModel";
import "components/Coordination/Glance/glance.css";
import { matchOption } from "components/SearchSelect/searchSelect";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

export default function TechnicalDocumentationPage() {
  const { projectId: routeProjectId } = useParams();
  const history = useHistory();
  const location = useLocation();
  useGlanceTheme();
  // ?print=1: the page as root-f5's PDF/thumbnail job renders it (headless
  // Chromium) - no chrome, just the view as the filter in the hash shows it.
  const printMode = new URLSearchParams(location.search).get("print") === "1";
  const [projects, setProjects] = useState([]);
  const [rememberedId, setRememberedId] = useLastProject(projects);
  const projectId = routeProjectId || rememberedId || "";
  const [views, setViews] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(() => { try { return new URLSearchParams(window.location.search).get("new") === "1"; } catch (e) { return false; } });
  // Guide 20.3: the short routes (a system chip, a zone, a floor plan, a
  // filtered register or a selection) open the same dialog pre-filled; the
  // pre-fill travels in the history state (glanceModel.openNewView).
  const [prefill, setPrefill] = useState(() => (location.state && location.state.newViewPrefill) || null);
  useEffect(() => {
    if (new URLSearchParams(location.search).get("new") !== "1") return;
    setPrefill((location.state && location.state.newViewPrefill) || null);
    setNewOpen(true);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const tab = (() => { try { return new URLSearchParams(location.search).get("tab") || "glance"; } catch (e) { return "glance"; } })();
  const setTab = (key) => history.replace(`/documentation/${projectId}${key === "glance" ? "" : `?tab=${encodeURIComponent(key)}`}`);

  useEffect(() => {
    let alive = true;
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { if (alive) setProjects(Array.isArray(rows) ? rows : (rows && rows.projects) || []); })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);

  // A bare /documentation opens on the remembered project.
  useEffect(() => { if (!routeProjectId && projectId) history.replace(`/documentation/${projectId}${location.search}`); }, [routeProjectId, projectId, history, location.search]);

  const loadViews = useCallback(() => {
    if (!projectId) return;
    viewsApi(projectId).then(setViews).catch(() => setViews({ views: [], canAdd: false, canEdit: false, canDelete: false }));
  }, [projectId]);
  useEffect(() => { setViews(null); setManifest(null); loadViews(); }, [loadViews]);

  // The building data, needed by every tab except the overview (which loads its own).
  useEffect(() => {
    if (!projectId || tab === "glance" || manifest) return;
    fetch(`${url}/projects/${projectId}/glance`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then((b) => { setManifest(b.manifest); setManifestError(""); })
      .catch((e) => setManifestError(e.message));
  }, [projectId, tab, manifest]);

  const viewTabs = ((views && views.views) || []).map((v) => ({
    key: v.viewId,
    label: v.title,
    children: manifest
      ? <TechnicalView key={v.viewId} projectId={projectId} manifest={manifest} viewId={v.viewId} onChanged={loadViews} onDeleted={() => { loadViews(); setTab("views"); }} />
      : manifestError ? <div className="g-empty">{manifestError}</div> : <Skeleton active paragraph={{ rows: 8 }} />,
  }));
  const known = ["glance", "views", "tracked", "processing", ...viewTabs.map((t) => t.key)];

  // One shared page body with the Review Center (root-f5's PageContainer):
  // centred, capped, the SaaS gutter left and right; the wide cap because
  // the schedules are broad tables.
  return (
    <PageContainer wide className={`bl-docs${printMode ? " g-print" : ""}`}>
      <div className="bl-docs-head">
        <h1>Technical documentation</h1>
        <Select filterOption={matchOption} showSearch size="small" style={{ minWidth: 260 }} value={projectId || undefined} placeholder="Choose a project" optionFilterProp="label"
                options={projects.map((p) => ({ value: String(p._id), label: p.title || p.name || "Untitled" }))}
                onChange={(v) => { setRememberedId(v); history.push(`/documentation/${v}`); }} />
        {/* technicalDocsAdd uploads and starts views; sharing stays with
            whoever may edit (the server's can* flags, 2026-10-14). */}
        {views && views.canAdd && projectId && (
          <>
            <UploadDocumentsButton projectId={projectId} projectTitle={(projects.find((p) => String(p._id) === String(projectId)) || {}).title}
                                   onUploaded={() => setTab("processing")} />
            <Button size="small" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>New view</Button>
          </>
        )}
        {views && views.canEdit && projectId && (
          <Button size="small" icon={<ShareAltOutlined />} onClick={() => setShareOpen(true)}>Share with a partner</Button>
        )}
      </div>
      {projectId && views && views.canAdd && <UploadGuide key={projectId} projectId={projectId} />}
      {!projectId ? (
        <div className="bl-glance"><div className="g-empty">Choose a project to see its documentation.</div></div>
      ) : (
        <Tabs activeKey={known.includes(tab) ? tab : "glance"} onChange={setTab} destroyInactiveTabPane
          items={[
            { key: "glance", label: "Project at a Glance", children: <GlanceTab key={projectId} projectId={projectId} onOpenDashboard={() => history.push(`/coordination/${projectId}`)} /> },
            {
              key: "views",
              label: `All views${views && views.views.length ? ` (${views.views.length})` : ""}`,
              children: (
                <div className="bl-glance">
                  <ViewCards projectId={projectId} data={views} manifest={manifest} onOpen={setTab} onNew={() => setNewOpen(true)} />
                </div>
              ),
            },
            {
              key: "tracked",
              label: "Tracked categories",
              children: <div className="bl-glance"><TrackedCategories projectId={projectId} /></div>,
            },
            {
              key: "processing",
              label: "Processing",
              children: <div className="bl-glance"><ProcessingPanel projectId={projectId} onBuildingData={() => setTab("glance")} /></div>,
            },
            ...viewTabs.map((t) => ({ ...t, children: <div className="bl-glance">{t.children}</div> })),
          ]} />
      )}
      {projectId && (
        <ShareResourceModal type="documentation" id={projectId} projectId={projectId} open={shareOpen}
                            title={`${(projects.find((p) => String(p._id) === String(projectId)) || {}).title || "Project"} — technical documentation`}
                            onClose={() => setShareOpen(false)} />
      )}
      {newOpen && manifest && (
        <NewViewModal open projectId={projectId} manifest={manifest} prefill={prefill} onClose={() => { setNewOpen(false); setPrefill(null); }}
                      onCreated={(view) => { setNewOpen(false); setPrefill(null); loadViews(); setTab(view.viewId); }} />
      )}
      {newOpen && !manifest && (
        <NewViewLoader projectId={projectId} onManifest={setManifest} onError={(e) => { setManifestError(e); setNewOpen(false); }} />
      )}
    </PageContainer>
  );
}

// Opening New view from the overview tab: fetch the building data first.
function NewViewLoader({ projectId, onManifest, onError }) {
  useEffect(() => {
    fetch(`${url}/projects/${projectId}/glance`, { headers: auth() })
      .then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`); return b; })
      .then((b) => onManifest(b.manifest)).catch((e) => onError(e.message));
  }, [projectId, onManifest, onError]);
  return null;
}
